import React, { useState, useEffect, useRef, useCallback } from "react"
import Link from "next/link"
import GoogleSignIn from "../GoogleSignIn"
import styles from "./Auth.module.css"
import { AuthType } from "../../types/enums"
import { useRouter }          from 'next/router'
import { useAuth }            from '../../src/auth'  
import { userIsCandidate,
         userIsEnterprise }   from 'src/helpers'
import { Auth } from 'aws-amplify'  
import { FileText, SlidersHorizontal, CirclePlay, ArrowRight } from "lucide-react"
import { captureEvent, identifyUser, usePostHogReady } from '@src/services/posthog/browser'
import { POSTHOG_EVENTS } from '@src/services/posthog/events'
import { getCognitoAttributionClientMetadata } from '@src/services/posthog/attribution'
import { identifyOpenAIAdsUser, trackOpenAIAdsRegistrationCompleted } from '@src/services/openaiAds/browser'
import { FREE_TRIAL_APPLICATION_CREDITS } from '@src/config/freeTrial'
import { getSignupInsertId } from '@src/services/posthog/identity'
import { getSignupAttribution, getSignupErrorCode, getSignupErrorMessage, getSignInErrorMessage, getRecoveryErrorMessage, isRegistrationUncertain, normalizeSignupEmail, SIGNUP_VERSION, validateSignup, validatePassword } from '@src/services/auth/signup'

const CANDIDATE_REDIRECT   = '/candidate/dashboard/auto'
const ENTERPRISE_REDIRECT  = '/employer/dashboard/jobs'

export default function AuthUI({ type, initialMode }: { type: AuthType; initialMode?: 'signUp' }) {
  const router = useRouter()

  const modeFromPath = (path: string) => {
    const clean = path.split('?')[0].replace(/\/+$/, '').toLowerCase()
    if (clean.endsWith('/signup'))    return 'signUp'
    if (clean.endsWith('/recovery') ||
        clean.endsWith('/recover'))   return 'recovery'
    return 'signIn'
  }

  const [mode, setMode] = useState<'signIn' | 'signUp' | 'recovery'>(() =>
    initialMode || modeFromPath(
      typeof window === 'undefined'
        ? router.pathname            
        : window.location.pathname  
    )
  );

  useEffect(() => {
    if (!router.isReady) return;
    setMode(modeFromPath(router.asPath));
  }, [router.isReady, router.asPath]);

  
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const googleLoadingRef = useRef(false)
  const authBusy = loading || googleLoading
  const onGoogleBusyChange = useCallback((value: boolean) => {
    googleLoadingRef.current = value
    setGoogleLoading(value)
  }, [])
  const submitting = useRef(false)
  const formRef = useRef<HTMLFormElement>(null)
  const signupViewCaptured = useRef(false)
  const posthogReady = usePostHogReady()
  const [showPassword, setShowPassword] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null)
  const [signupErrorCode, setSignupErrorCode] = useState<string | null>(null)
  const { signInUser, signOutUser, user } = useAuth()
  const [recoveryStep, setStep]     = useState<'email'|'code'|'success'>('email')
  const [verificationCode, setVerificationCode] = useState("");
  const [info,         setInfo]         = useState<string | null>(null);
  const [canResend,    setCanResend]    = useState(true);

  useEffect(() => {
    if (mode !== 'signUp') {
      signupViewCaptured.current = false
      return
    }
    if (!posthogReady || signupViewCaptured.current) return
    signupViewCaptured.current = captureEvent(POSTHOG_EVENTS.SIGNUP_PAGE_VIEWED, {
      user_group: type === AuthType.Candidate ? 'candidate' : 'enterpriseowner',
      signup_version: SIGNUP_VERSION,
      path: router.pathname,
      ...getSignupAttribution(),
    })
  }, [mode, posthogReady, router.pathname, type])

  /* ░░░ resend handler ░░░ */
  async function resendRecoveryEmail() {
    if (!canResend || !email) return;         // guard-rails
    try {
      setCanResend(false);                    // cool-down (optional)
      await Auth.forgotPassword(email);       // fire the email
      setInfo('Verification code sent!');     // ✓ feedback
      setTimeout(() => setCanResend(true), 30_000);   // 30-sec cool-down
    } catch {
      setCanResend(true);
      setError('Could not resend code. Try again.');
    }
  }

  async function doSignIn() {
    if (!email.trim()) {
      setError('Email address is required')
      return
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address')
      return
    }
    
    if (!password) {
      setError('Password is required')
      return
    }

    setLoading(true)
    try {
      const meta = { userType: type }   // you used this earlier
      await signInUser(normalizeSignupEmail(email), password, meta, null)
    } catch (err) {
      setError(getSignInErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  async function doSignUp () {
    const normalizedEmail = normalizeSignupEmail(email)
    let accountCreated = registeredEmail === normalizedEmail
    const attribution = getSignupAttribution()
    const signupProperties = {
      user_group: type === AuthType.Candidate ? 'candidate' : 'enterpriseowner',
      signupMethod: 'password',
      signup_version: SIGNUP_VERSION,
      ...attribution,
    }
    captureEvent(POSTHOG_EVENTS.SIGNUP_SUBMITTED, {
      ...signupProperties,
      action: accountCreated ? 'sign_in_existing_registration' : 'create_account',
    })
    const validation = validateSignup(normalizedEmail, password)
    if (validation) {
      setError(validation.message)
      captureEvent(POSTHOG_EVENTS.SIGNUP_VALIDATION_FAILED, { ...signupProperties, reason: validation.reason })
      formRef.current?.querySelector<HTMLInputElement>(`[name="${validation.field}"]`)?.focus()
      return
    }
    const startedAt = Date.now()
    setLoading(true)
    setError(null)
    setSignupErrorCode(null)
    try {
      if (!accountCreated) {
        captureEvent(POSTHOG_EVENTS.SIGNUP_STARTED, signupProperties)
        const signUpResult = await Auth.signUp({
          username: normalizedEmail,
          password,
          attributes: {},
          clientMetadata: {
            userGroup: signupProperties.user_group,
            ...(type === AuthType.Enterprise && typeof router.query.promo === 'string' ? { promo: router.query.promo } : {}),
            ...getCognitoAttributionClientMetadata(attribution),
          },
        })
        accountCreated = true
        setRegisteredEmail(normalizedEmail)
        identifyUser(signUpResult, { user_group: signupProperties.user_group })
        captureEvent(POSTHOG_EVENTS.USER_SIGNED_UP, {
          ...signupProperties,
          '$insert_id': getSignupInsertId(signUpResult),
          userId: signUpResult?.userSub,
          duration_ms: Date.now() - startedAt,
        })
        // Advertising delivery must not delay or fail the authenticated handoff.
        void identifyOpenAIAdsUser({ email: normalizedEmail, externalId: signUpResult?.userSub })
          .then(() => trackOpenAIAdsRegistrationCompleted({ userId: signUpResult?.userSub }))
          .catch(() => {})
      }
      // The auth provider owns authenticated state and the dashboard redirect.
      await signInUser(normalizedEmail, password, { userType: type }, null)
    } catch (err) {
      const code = getSignupErrorCode(err)
      setSignupErrorCode(code)
      setError(getSignupErrorMessage(err, accountCreated))
      captureEvent(POSTHOG_EVENTS.SIGNUP_FAILED, {
        ...signupProperties,
        action: accountCreated ? 'sign_in' : 'create_account',
        status: accountCreated ? 'account_created' : isRegistrationUncertain(err) ? 'registration_unconfirmed' : 'account_not_created',
        error_code: code,
        duration_ms: Date.now() - startedAt,
      })
    } finally {
      setLoading(false)
    }
  }
  
  async function sendRecoveryEmail() {
    if (!email) { setError('Enter a valid e-mail'); return }
    try {
      setLoading(true)
      await Auth.forgotPassword(email)
      setStep('code')
      setError(null)
    } catch (e) {
      setError('Could not send code. Try again.')
    } finally { setLoading(false) }
  }
  
  async function submitCodeAndPassword() {
    const validation = validatePassword(password);
    if (validation) {
      setError(validation.message);
      formRef.current?.querySelector<HTMLInputElement>('#recovery-password')?.focus();
      return;
    }
    if (!verificationCode.trim()) { setError('Enter the verification code from your email.'); return; }
    if (!password || password !== confirmPassword) {
      setError("Passwords must match");
      return;
    }
    try {
      setLoading(true);
      /*              ↓ use the dedicated code variable  */
      await Auth.forgotPasswordSubmit(email, verificationCode, password);
      setStep("success");
      setError(null);
    } catch (e) {
      setError(getRecoveryErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  function doRecovery() {
    // dispatches according to the current step
    if (recoveryStep === 'email')   return sendRecoveryEmail()
    if (recoveryStep === 'code')    return submitCodeAndPassword()
  }
  
  useEffect(() => {
    if (!user) return
    if (userIsCandidate(user))   router.push(CANDIDATE_REDIRECT)
    else if (userIsEnterprise(user)) router.push(ENTERPRISE_REDIRECT)
    else signOutUser()
  }, [user])
  
  useEffect(() => {
    if (mode !== 'recovery') {
      setStep('email');   // always reset the wizard
    }
  }, [mode]);

  useEffect(() => {
    setError(null);
  }, [mode, recoveryStep]);

  useEffect(() => {
    setError(null);
    setInfo(null);                // ← clear on step / mode change
  }, [mode, recoveryStep]);
  
  /* PRIMARY button dispatcher */
  async function handleAuthAction(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting.current || loading || googleLoadingRef.current) return
    submitting.current = true
    try {
      if (mode === 'signIn') await doSignIn()
      if (mode === 'signUp') await doSignUp()
      if (mode === 'recovery') await doRecovery()
    } finally {
      submitting.current = false
    }
  }

  const isCandidate = type === AuthType.Candidate
  const isSignup = mode === 'signUp'
  const isRecovery = mode === 'recovery'
  const recoveryComplete = isRecovery && recoveryStep === 'success'
  const title = isRecovery
    ? recoveryComplete ? 'Password updated' : recoveryStep === 'code' ? 'Check your email' : 'Reset your password'
    : isSignup
      ? isCandidate ? 'Create your free account' : 'Create an employer account'
      : isCandidate ? 'Welcome back' : 'Welcome back, employer'
  const description = isRecovery
    ? recoveryComplete ? 'Your new password is ready. Sign in to continue.'
      : recoveryStep === 'code' ? 'Enter the verification code we sent and choose a new password.'
      : 'Enter your email and we’ll send a verification code.'
    : isSignup
      ? isCandidate ? `Start with ${FREE_TRIAL_APPLICATION_CREDITS} free application credits. No credit card required.` : 'Set up your account to get started with LifeShack.'
      : isCandidate ? 'Sign in to pick up your job search.' : 'Sign in to your LifeShack hiring workspace.'
  const buttonText = isSignup
    ? registeredEmail === normalizeSignupEmail(email) ? 'Continue to account' : isCandidate ? 'Create free account' : 'Create account'
    : isRecovery ? recoveryStep === 'code' ? 'Update password' : 'Send verification code'
    : 'Sign in'

  function renderEmail() {
    return (
      <div className={styles.formField}>
        <label htmlFor="auth-email">Email address</label>
        <input
          className={styles.textInput}
          id="auth-email"
          name="email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
          spellCheck={false}
          inputMode="email"
          placeholder="you@example.com"
          onChange={event => setEmail(event.target.value)}
          value={email}
          disabled={authBusy}
        />
      </div>
    )
  }

  function renderPassword() {
    return (
      <div className={styles.formField}>
        <div className={styles.labelRow}>
          <label htmlFor="auth-password">Password</label>
          {mode === 'signIn' && (
            <button type="button" className={styles.textAction} onClick={() => setMode('recovery')}>Forgot password?</button>
          )}
        </div>
        <div className={styles.passwordField}>
          <input
            className={styles.textInput}
            id="auth-password"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete={isSignup ? 'new-password' : 'current-password'}
            aria-describedby={isSignup ? 'signup-password-hint' : undefined}
            value={password}
            onChange={event => setPassword(event.target.value)}
            disabled={authBusy}
          />
          <button
            className={styles.visibilityControl}
            type="button"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            aria-pressed={showPassword}
            onClick={() => setShowPassword(current => !current)}
          >
            {showPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {isSignup && <p id="signup-password-hint" className={styles.fieldHint}>Use at least 8 characters.</p>}
      </div>
    )
  }

  return (
    <div className={styles.authPage}>
      <div className={styles.pageInner}>
        <header className={styles.pageHeader}>
          <Link href="/" passHref>
            <a className={styles.brandLink} aria-label="LifeShack home">
              <img src="/assets/brand/PrimaryLogoPurple.svg" alt="LifeShack" width={944} height={281} />
            </a>
          </Link>
          <Link href="/" passHref>
            <a className={styles.backToSite}>Back to LifeShack <ArrowRight size={14} aria-hidden="true" /></a>
          </Link>
        </header>

        <div className={styles.pageColumns}>
          <section className={styles.formPanel} aria-labelledby="auth-title">
            <form className={styles.authForm} noValidate onSubmit={handleAuthAction} ref={formRef} aria-busy={authBusy}>
              <fieldset className={styles.formFields} disabled={authBusy}>
                <div className={styles.formIntro}>
                  <span className={styles.eyebrow}>{isRecovery ? 'Account recovery' : isSignup ? 'Let’s get started' : 'Your LifeShack account'}</span>
                  <h1 id="auth-title" className={styles.formTitle}>{title}</h1>
                  <p className={styles.formDescription}>{description}</p>
                </div>

                {!isRecovery && isCandidate && (
                  <div className={styles.socialSection}>
                    <GoogleSignIn authFlow={mode} userGroup="candidate" disabled={loading} onBusyChange={onGoogleBusyChange} />
                    <div className={styles.emailDivider}><span>or continue with email</span></div>
                  </div>
                )}

                {error && (
                  <div role="alert" className={styles.errorNotice}>
                    {error}
                    {isSignup && (signupErrorCode === 'UsernameExistsException' || (signupErrorCode && isRegistrationUncertain({code: signupErrorCode})) || registeredEmail === normalizeSignupEmail(email)) && (
                      <div className={styles.errorActions}>
                        <button type="button" onClick={() => setMode('signIn')}>Sign in</button>
                        <button type="button" onClick={() => setMode('recovery')}>Reset password</button>
                      </div>
                    )}
                  </div>
                )}
                {info && <div role="status" className={styles.infoNotice}>{info}</div>}

                <div className={styles.fieldStack}>
                  {(!isRecovery || recoveryStep === 'email') && renderEmail()}
                  {!isRecovery && renderPassword()}
                  {isRecovery && recoveryStep === 'code' && (
                    <>
                      <div className={styles.formField}>
                        <div className={styles.labelRow}>
                          <label htmlFor="recovery-code">Verification code</label>
                          <button type="button" className={styles.textAction} onClick={resendRecoveryEmail} disabled={!canResend || authBusy}>
                            {canResend ? 'Resend code' : 'Code sent'}
                          </button>
                        </div>
                        <input className={styles.textInput} id="recovery-code" name="code" autoComplete="one-time-code"
                          value={verificationCode} onChange={event => setVerificationCode(event.target.value)} />
                      </div>
                      <div className={styles.formField}>
                        <label htmlFor="recovery-password">New password</label>
                        <input className={styles.textInput} id="recovery-password" name="password" type="password" autoComplete="new-password"
                          value={password} onChange={event => setPassword(event.target.value)} />
                      </div>
                      <div className={styles.formField}>
                        <label htmlFor="recovery-confirm">Confirm new password</label>
                        <input className={styles.textInput} id="recovery-confirm" name="confirmPassword" type="password" autoComplete="new-password"
                          value={confirmPassword} onChange={event => setConfirmPassword(event.target.value)} />
                      </div>
                    </>
                  )}
                </div>

                {!recoveryComplete && (
                  <button type="submit" className={styles.submitButton} disabled={authBusy}>
                    {authBusy ? (
                      <>
                        <span className={styles.loadingSpinner} aria-hidden="true" />
                        {googleLoading ? 'Opening Google…' : isRecovery ? 'Please wait…' : isSignup ? 'Creating account…' : 'Signing in…'}
                      </>
                    ) : buttonText}
                    {!authBusy && <ArrowRight size={16} aria-hidden="true" />}
                  </button>
                )}

                {isSignup && isCandidate && (
                  <p className={styles.activationNote}>
                    First, add your resume and job preferences. Applications only start when you activate Auto Apply.
                  </p>
                )}

                {isSignup && (
                  <p className={styles.legalNote}>
                    By creating an account, you agree to our{' '}
                    <Link href="/terms/" passHref><a>Terms of Service</a></Link> and{' '}
                    <Link href="/privacy/" passHref><a>Privacy Policy</a></Link>.
                  </p>
                )}

                <div className={styles.modeSwitch}>
                  {isRecovery ? (
                    <button className={styles.textAction} type="button" onClick={() => setMode('signIn')}>Back to sign in</button>
                  ) : isSignup ? (
                    <>Already have an account? <button className={styles.textAction} type="button" onClick={() => setMode('signIn')}>Sign in</button></>
                  ) : (
                    <>New to LifeShack? <button className={styles.textAction} type="button" onClick={() => setMode('signUp')}>Create an account</button></>
                  )}
                </div>
              </fieldset>
            </form>
          </section>

          <aside className={styles.editorialPanel} aria-label={isCandidate ? 'How LifeShack works' : 'LifeShack for employers'}>
            <div className={styles.editorialCopy}>
              <span className={styles.eyebrow}>{isCandidate ? 'A little less busywork' : 'LifeShack for employers'}</span>
              <h2 className={styles.editorialTitle}>{isCandidate ? <>Your search.<br />A little lighter.</> : <>Your next team.<br />Starts here.</>}</h2>
              <p className={styles.editorialDescription}>
                {isCandidate
                  ? 'Turn your experience and preferences into a job search that works for you.'
                  : 'A dedicated workspace for your company’s hiring.'}
              </p>
            </div>
            {isCandidate ? (
              <div className={styles.workflow}>
                <div className={styles.workflowHeading}>Your setup, step by step<span>01 — 03</span></div>
                <ol className={styles.workflowList}>
                  <li>
                    <span className={styles.workflowIcon}><FileText size={21} strokeWidth={1.6} aria-hidden="true" /></span>
                    <div><h3>Add your resume</h3><p>Bring your experience into your profile.</p></div>
                    <span className={styles.stepNumber}>01</span>
                  </li>
                  <li>
                    <span className={styles.workflowIcon}><SlidersHorizontal size={21} strokeWidth={1.6} aria-hidden="true" /></span>
                    <div><h3>Make it your search</h3><p>Choose your roles, locations, and preferences.</p></div>
                    <span className={styles.stepNumber}>02</span>
                  </li>
                  <li>
                    <span className={styles.workflowIcon}><CirclePlay size={21} strokeWidth={1.6} aria-hidden="true" /></span>
                    <div><h3>Start when you’re ready</h3><p>You decide when to activate Auto Apply.</p></div>
                    <span className={styles.stepNumber}>03</span>
                  </li>
                </ol>
                <div className={styles.workflowFooter}><span className={styles.statusDot} />Applications stay off until you activate.</div>
              </div>
            ) : (
              <div className={styles.employerMark} aria-hidden="true"><span>L</span><span>S</span></div>
            )}
            <div className={styles.editorialFooter}>Less repetition. More possibility.</div>
          </aside>
        </div>
      </div>
    </div>
  )
}
